import {getCachedConfiguration} from '@nuskin/configuration-sdk';
import {RunConfigService} from '@nuskin/ns-util';
import {OrderManager, CartService, OrderType} from '../shop';
import axios from 'axios';

let PickupUtil = function() {
    'use strict';
    const SERVICE_GROUP = 'SERVICE_GROUP',
        PICKUPID = 'PICKUPID',
        CARRIER_NAME = 'CARR_SRV_NAME',
        NAME = 'NAME1',
        STREET = 'STREET',
        POSTALCODE = 'PSTLZ',
        CITY = 'ORT01',
        OPENHRS = 'OPENHRS',
        LATITUDE = 'LATITUDE',
        LONGITUDE = 'LONGITUDE',
        DISTANCE = 'DISTANCE',
        LOCKER = 'SRCODE',
        UNIT = 'UNIT',
        CARRIER_CODE = 'CARRIER_CODE';

    let promise = Promise.resolve();

    /**
     * Pickup Point shipping methods.
     * Values are shipping method codes.
     */
    const shippingMethods = {
        UPS: 'K1',
        BRING: 'K2',
        METAPACK: 'M3'
    };

    /**
     * Returns `true` if the given shipping method is a Pickup Point method, `false` otherwise.
     * @param {string} code Shipping method code
     * @returns {boolean}
     */
    function isPickupPointShippingMethod(code) {
        return Object.values(shippingMethods).includes(code);
    }

    /**
     * Parses the given Custom fields to create a list of Pickup Points.
     * @param {CustomField[]} customData Custom Fields from SAP Sales Order Detail object
     * @returns {PickupPoint[]} List of Pickup Points
     */
    function parsePickupPoints(customData) {
        let pickupPoints = [];

        const serviceGroups = customData.filter(item => item.Key === SERVICE_GROUP);
        const ids = customData.filter(item => item.Key === PICKUPID);
        const carriers = customData.filter(item => item.Key === CARRIER_NAME);
        const names = customData.filter(item => item.Key === NAME);
        const streets = customData.filter(item => item.Key === STREET);
        const cities = customData.filter(item => item.Key === CITY);
        const postalCodes = customData.filter(item => item.Key === POSTALCODE);
        const distances = customData.filter(item => item.Key === DISTANCE);
        const lockers = customData.filter(item => item.Key === LOCKER);
        const units = customData.filter(item => item.Key === UNIT);
        const openHours = customData.filter(item => item.Key === OPENHRS);
        const latitudes = customData.filter(item => item.Key === LATITUDE);
        const longitudes = customData.filter(item => item.Key === LONGITUDE);
        const carrierCodes = customData.filter(item => item.Key === CARRIER_CODE);

        for (let i = 0; i < ids.length; i++) {
            pickupPoints.push({
                serviceGroup: serviceGroups && serviceGroups[i] ? serviceGroups[i].Value : undefined,
                id: ids[i].Value,
                carrier: carriers && carriers[i] ? carriers[i].Value : undefined,
                name: names && names[i] ? names[i].Value : undefined,
                street: streets && streets[i] ? streets[i].Value : undefined,
                city: cities && cities[i] ? cities[i].Value : undefined,
                postalCode: postalCodes && postalCodes[i] ? postalCodes[i].Value : undefined,
                distance: distances && distances[i] ? distances[i].Value : undefined,
                locker: lockers && lockers[i] ? lockers[i].Value : undefined,
                unit: units && units[i] ? units[i].Value : undefined,
                openingHours: openHours && openHours[i] ? openHours[i].Value : undefined,
                latitude: latitudes && latitudes[i] ? parseFloat(latitudes[i].Value) : undefined,
                longitude: longitudes && longitudes[i] ? parseFloat(longitudes[i].Value) : undefined,
                carrierCode: carrierCodes && carrierCodes[i] ? carrierCodes[i].Value : undefined
            });
        }
        pickupPoints.sort((a, b) => parseFloat(a.distance) - parseFloat(b.distance));
        pickupPoints.forEach((point, idx) => point.icon = `https://chart.apis.google.com/chart?chst=d_map_pin_letter&chld=${idx + 1}|008AB0|FFFFFF`);

        return pickupPoints;
    }

    /**
     * Creates a Google Maps LatLngBounds instance that encloses the given GPS coordinates.
     *
     * IMPORTANT NOTE: this function expects the Google Maps API to be loaded.
     * @param {{latitude: number, longitude: number}[]} points List of GPS coordinates to enclose
     * @returns {google.maps.LatLngBounds} Bounding box containing the given points
     */
    function getBounds(points) {
        if (!Array.isArray(points)) {
            throw 'No points supplied; cannot calculate center';
        }

        return points.reduce((bounds, point) => {
            return bounds.extend({
                lat: point.latitude,
                lng: point.longitude
            });
        }, new google.maps.LatLngBounds()); // eslint-disable-line
    }

    /**
     * Creates a list of map marker data objects for the given Pickup Points.
     * @param {PickupPoint[]} points List of Pickup Points
     * @returns {PickupPointMarker[]}
     */
    function generateMarkers(points) {
        if (!Array.isArray(points)) {
            throw 'No points supplied; cannot generate markers';
        }

        let markers = points.map((point, index) => {
            return {
                id: point.id,
                index: index,
                icon: point.icon,
                name: point.name,
                street: point.street,
                postalCode: point.postalCode,
                city: point.city,
                carrier: point.carrier,
                distance: point.distance,
                position: [point.latitude, point.longitude],
                openingHours: point.openingHours,
                locker: point.locker
            };
        });
        return markers;
    }

    function getOrderValue() {
        let value = 0;
        const orderType = OrderManager.getOrder().orderType;
        const cartInfo = CartService.getCartInfo();

        if (orderType === OrderType.SINGLE_ORDER) {
            value = cartInfo.totalOrderPrice;
        } else {
            value = cartInfo.totalOrderPrice + cartInfo.totalAdrPrice;
        }

        return value;
    }

    function getUrl() {
        switch (RunConfigService.getEnvironmentCode()) {
            case 'dev':
                return 'https://apis.dev.nuskin.com/ship-methods/v1';
            case 'test':
                return 'https://apis.test.nuskin.com/ship-methods/v1';
            case 'prod':
                return 'https://apis.nuskin.com/ship-methods/v1';
            default:
                return 'https://apis.test.nuskin.com/ship-methods/v1';
        }
    }

    function getShipToData(order) {
        const shippingParties = (order && order.shipping && order.shipping.shippingParties) || [];
        const shipTo = shippingParties.find((sp) => sp.type === 'SHIP_TO');
        return shipTo ? {
            country: shipTo.shippingAddress.address.countryCode,
            postalCode: shipTo.shippingAddress.address.postalCode,
            line1: `${shipTo.shippingAddress.address.street1}, ${shipTo.shippingAddress.address.city}`,
            line2: shipTo.shippingAddress.address.street2,
            city: shipTo.shippingAddress.address.city
        } : {};
    }

    function samePayload(payload) {
        let same = false;
        const shipping = sessionStorage.getItem('shipping');

        if (shipping) {
            const storedPayload = JSON.parse(shipping).payload;
            const shipData = getShipToData(payload.order);
            const storedShipData = getShipToData(storedPayload.order);
            same = payload.country === storedPayload.country &&
                payload.dangerousGoods === storedPayload.dangerousGoods &&
                shipData.country === storedShipData.country &&
                shipData.postalCode === storedShipData.postalCode &&
                shipData.line1 === storedShipData.line1 &&
                shipData.city === storedShipData.city
        }
        return same
    }

    function loadShipMethods(order, orderType) {
        const {metapackMarket} = getCachedConfiguration('Checkout');

        if (metapackMarket) {
            const runConfig = RunConfigService.getRunConfig();
            const payload = {
                order,
                orderValue: getOrderValue(),
                dangerousGoods: CartService.hasDangerousGoods(orderType),
                weight: CartService.getCartWeight(orderType),
                language: runConfig.language,
                country: runConfig.country
            };
            if (!samePayload(payload)) {
                promise = axios.post(
                    getUrl(),
                    payload,
                    {
                        headers: {
                            'Content-Type': 'application/json; charset=UTF-8'
                        },
                        timeout: 29000
                    }
                ).then((results) => {
                    sessionStorage.setItem('shipping', JSON.stringify({
                        payload,
                        shipMethods: results.data.shipMethods,
                        pickUpPoints: results.data.pickupPoints
                    }))
                }).catch((err) => {
                    console.log(err);
                    sessionStorage.setItem('shipping', JSON.stringify({payload: {}, shipMethods: [], pickUpPoints: []}));
                });
            }
        }
    }

    // if the calling code knows that loadShipMethods has finished then this
    // function can be called directly if the calling function is not async.
    function getStoredShipMethods() {
        const shipping = sessionStorage.getItem('shipping');
        return shipping ? JSON.parse(shipping).shipMethods : [];
    }

    async function getShipMethods() {
        await promise;

        return getStoredShipMethods();
    }

    async function getPickupPoints() {
        await promise;

        const shipping = sessionStorage.getItem('shipping');
        return shipping ? JSON.parse(shipping).pickUpPoints : [];
    }

    return {
        allMethods: shippingMethods,
        supports: isPickupPointShippingMethod,
        parse: parsePickupPoints,
        bounds: getBounds,
        markers: generateMarkers,
        loadShipMethods,
        getShipMethods,
        getStoredShipMethods,
        getPickupPoints
    }
}();

export default PickupUtil;

/**
 * Object representing a custom data field used in communication with Nu Skin Shop APIs.
 * This is used to pass a data value from client to server or vice-versa, where the value
 * in question doesn't fit into the existing API data fields.
 * @typedef CustomField
 * @property {string} Type - Custom field type
 * @property {string} Key - Custom field key name
 * @property {string} Value - Custom field value
 */

/**
 * Object representing a Pickup Point location.
 * @typedef PickupPoint
 * @property {string} id - ID of the Pickup Point location
 * @property {string} name - Name of the Pickup Point location
 * @property {string} [locker] - Locker number (if applicable)
 * @property {string} street - Street address
 * @property {string} city - City
 * @property {string} postalCode - Postal/Zip code
 * @property {string[]} openingHours - List of opening hours for the Pickup Point location
 * @property {string} distance - Distance of the Pickup Point from the reference address
 * @property {string} unit - Unit of measurement for the distance figure
 * @property {number} latitude - Latitude of the Pickup Point location
 * @property {number} longitude - Longitude of the Pickup Point location
 * @property {string} icon - Marker icon URL
 */

/**
 * Pickup Point map marker data.
 * @typedef PickupPointMarker
 * @property {number} index - Index of the Pickup Point in the list
 * @property {string} icon - Marker icon URL
 * @property {string} id - ID of the Pickup Point location
 * @property {string} name - Name of the Pickup Point location
 * @property {string} [locker] - Locker number (if applicable)
 * @property {string} street - Street address
 * @property {string} city - City
 * @property {string} postalCode - Postal/Zip code
 * @property {string[]} openingHours - List of opening hours for the Pickup Point location
 * @property {string} distance - Distance of the Pickup Point from the reference address
 * @property {number[]} position - Latitude-longitude pair
 */
